import { useState } from 'react'

import { useToast } from '@/components/Toast'
import { AlertModal } from '@/components/ui/AlertModal'
import { CTAButton } from '@/components/ui/CTAButton'
import { DialogModal } from '@/components/ui/DialogModal'
import { OutlineButton } from '@/components/ui/OutlineButton'
import { getUserFacingMessage } from '@/lib/errorMessage'
import { CANCEL_NOT_ALLOWED_DIALOG, CHANGE_NOT_ALLOWED_DIALOG } from '@/pages/SealedVinylPage/lib/constants'
import { isRegisteredAtChangeExpired } from '@/pages/SealedVinylPage/lib/utils'
import { isCancelNotAllowedError } from '@/payment/constants'
import { useDeleteOrder } from '@/payment/query'
import type { GetSealedOrderDetailResponse } from '@/payment/types'

import { DetailNotice } from '../DetailNotice'
import { AddressSection, HeadingSection, ItemSection, PaymentInfoSection } from '../sections'

import { ActionRow, DetailBottomBar, DetailContainer, DetailContents, SectionDivider } from './styles'

interface OrderDetailViewProps {
  detail: GetSealedOrderDetailResponse
  onEdit: () => void
  onCancelled: () => void
}

/** 기한 만료·서버 거부로 차단된 액션 — 안내 Dialog 분기용 */
type NotAllowedAction = 'edit' | 'cancel'

export function OrderDetailView({ detail, onEdit, onCancelled }: OrderDetailViewProps) {
  const { orderInfo, deliveryInfo, paymentInfo } = detail
  const { mutate: deleteOrder, isPending: isDeleting } = useDeleteOrder()
  const { show: showToast } = useToast()

  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false)
  const [notAllowed, setNotAllowed] = useState<NotAllowedAction | null>(null)

  const handleEditClick = () => {
    if (isRegisteredAtChangeExpired(orderInfo.registeredAt)) {
      setNotAllowed('edit')
      return
    }
    onEdit()
  }

  const handleCancelClick = () => {
    setIsCancelConfirmOpen(true)
  }

  const handleCancelClose = () => {
    setIsCancelConfirmOpen(false)
  }

  const handleNotAllowedClose = () => {
    setNotAllowed(null)
  }

  const handleCancelConfirm = () => {
    setIsCancelConfirmOpen(false)
    if (isRegisteredAtChangeExpired(orderInfo.registeredAt)) {
      setNotAllowed('cancel')
      return
    }
    deleteOrder(orderInfo.orderId, {
      onSuccess: () => {
        showToast('신청이 취소됐어요')
        onCancelled()
      },
      onError: (error) => {
        if (isCancelNotAllowedError(error)) {
          setNotAllowed('cancel')
          return
        }
        showToast(getUserFacingMessage(error))
      },
    })
  }

  return (
    <>
      <DetailContainer>
        <DetailContents>
          <HeadingSection registeredAt={orderInfo.registeredAt} />
          <SectionDivider />
          <AddressSection deliveryInfo={deliveryInfo} />
          <SectionDivider />
          <ItemSection quantity={orderInfo.quantity} />
          <SectionDivider />
          <PaymentInfoSection paymentInfo={paymentInfo} />
          <SectionDivider />
          <DetailNotice />
        </DetailContents>
        <DetailBottomBar>
          <ActionRow>
            <OutlineButton
              variant='danger'
              loading={isDeleting}
              onClick={handleCancelClick}
            >
              신청 취소
            </OutlineButton>
            <CTAButton
              disabled={isDeleting}
              onClick={handleEditClick}
            >
              수량 변경
            </CTAButton>
          </ActionRow>
        </DetailBottomBar>
      </DetailContainer>
      <AlertModal
        isOpen={isCancelConfirmOpen}
        onClose={handleCancelClose}
        title='밀봉비닐 신청을 취소할까요?'
        description='취소하면 입력한 정보는 사라져요'
        primaryAction={{ label: '취소하기', onClick: handleCancelConfirm, variant: 'danger' }}
        secondaryAction={{ label: '닫기', onClick: handleCancelClose }}
      />
      {notAllowed === 'edit' && (
        <DialogModal
          isOpen
          onClose={handleNotAllowedClose}
          {...CHANGE_NOT_ALLOWED_DIALOG}
          confirmLabel='확인'
          onConfirm={handleNotAllowedClose}
        />
      )}
      {notAllowed === 'cancel' && (
        <DialogModal
          isOpen
          onClose={handleNotAllowedClose}
          {...CANCEL_NOT_ALLOWED_DIALOG}
          confirmLabel='확인'
          onConfirm={handleNotAllowedClose}
        />
      )}
    </>
  )
}
